import { Empty, RefSelectProps, Select, SelectProps, Typography } from 'antd';
import { forwardRef, ForwardRefRenderFunction, useEffect, useMemo, useState } from 'react';
import { useGetClientGroupsQuery } from '../../../modules/client-groups/api/client-groups-api';
import { useLazyGetCustomersQuery } from '../../../modules/customers/api/customers-api';
import { TeamIcon } from '../../icons/team-icon';
import { filterValueByCriteria } from '../../utils/filter-value-by-criteria/filter-value-by-criteria';
import PageSpinner from '../page-spinner/page-spinner';
import { useDebounceValue } from 'usehooks-ts';

interface Value {
  customers: string[];
  groups: string[];
}

interface Props extends Omit<SelectProps, 'filterOption' | 'showSearch' | 'onSearch' | 'onBlur' | 'onChange'> {
  value?: Value;
  onChange?: (value: Value) => void;
}

const CustomersClientGroupsSelect: ForwardRefRenderFunction<RefSelectProps, Props> = (
  { value, onChange, ...props },
  ref,
): JSX.Element => {
  const [getCustomers, { data: customers, isFetching: isCustomersFetching }] = useLazyGetCustomersQuery();
  const [customerSearchValue, setCustomerSearchValue] = useState('');
  const [debouncedValue] = useDebounceValue(customerSearchValue, 400);

  const { data: clientGroups, isFetching: isClientGroupsFetching } = useGetClientGroupsQuery({
    page: 1,
    limit: 999,
  });

  const groupsContainsUsers = useMemo(
    () =>
      clientGroups?.items.filter(({ customers, name }) => customers > 0 && filterValueByCriteria(name, debouncedValue)),
    [clientGroups, debouncedValue],
  );

  const groupsContainsUsersIds = useMemo(() => groupsContainsUsers?.map(group => group.id), [groupsContainsUsers]);

  const handleSearch = (searchValue: string) => {
    setCustomerSearchValue(searchValue);
  };

  const handleChange = (optionValues: string[]) => {
    const formattedSelectedOptions = optionValues.reduce(
      (acc, id) => {
        if (groupsContainsUsersIds?.includes(id)) {
          acc.groups.push(id);
        } else {
          acc.customers.push(id);
        }
        return acc;
      },
      { groups: [], customers: [] } as Value,
    );

    onChange?.(formattedSelectedOptions);
  };

  useEffect(() => {
    getCustomers({ page: 1, limit: 50, fullName: debouncedValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  return (
    <Select
      ref={ref}
      allowClear={true}
      showSearch
      value={value ? [...value.customers, ...value.groups] : []}
      onSearch={handleSearch}
      filterOption={false}
      onChange={handleChange}
      {...props}
      notFoundContent={
        isCustomersFetching || isClientGroupsFetching ? (
          <PageSpinner size="small" />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )
      }
    >
      <>
        {groupsContainsUsers?.map(({ id, name }) => (
          <Select.Option key={id} value={id}>
            <TeamIcon /> {name}
          </Select.Option>
        ))}
        {customerSearchValue &&
          customers?.items.map(({ id, name, email }) => (
            <Select.Option key={id} value={id}>
              {name} – <Typography.Text italic>{email}</Typography.Text>
            </Select.Option>
          ))}
      </>
    </Select>
  );
};

export default forwardRef(CustomersClientGroupsSelect);
